/*
 * Copyright (c) 2023
 *
 * The source code and all documents related to the source code ("Material") are owned by
 * Frictionless Systems, LLC.
 *
 * The Material contains trade secrets, proprietary information, and confidential information.
 * No part of the Material may be used, copied, reproduced, modified, published, distributed, or
 * disclosed in any way without Frictionless Systems, LLC’s express prior written permission.
 *
 * The Material may be protected under patent, trademark, copyright, or trade secret laws.  No
 * license is granted to you by disclosure or delivery of the Materials.  Any license must be
 * express and approved by Frictionless Systems, LLC in writing.
 *
 * “Frictionless Systems” and “Impulse” are registered trademarks of Frictionless Systems, LLC.
 */

package systems.frictionless.impulse.browser.id

import com.benasher44.uuid.Uuid
import systems.frictionless.impulse.rest.model.Index

/**
 * IDs for various UI elements.  These primarily exist as a hook for automated testing to find elements on the screen
 * in a way that's more stable than Xpath queries.
 */
enum class HtmlId(val idString: String) {
    LinkForgotPassword("forgot_password"),

    Headline("headline"),
    FormFieldEmail("email"),
    FormFieldPassword("password"),
    FormFieldFirstName("firstname"),
    FormFieldLastName("lastname"),
    ErrorMessage("error"),
    PrimaryButton("button_next"),
    ProgressButton("button_progress"),

    // Tag something unique on each page transition, to make it easier to verify the page has been loaded
    SectionIntro("section_intro"),
    SectionLogin("section_login"),
    SectionDemographicsOverview("section_demographics_overview"),
    SectionDemographicsQuestions("section_demographics_questions"),
    SectionQuestionsOverview("section_questions_overview"),
    SectionQuestionsQuestions("section_questions_questions"),
    SectionInsights("section_insights"),

    // These are outside of the numbered list, so they aren't called "Sections"
    PageForgotPassword("page_forgot_password"),
    PageChangePassword("page_change_password"),
    PageDeleteAccount("page_delete_account"),
    ForgotPasswordSuccess("forgot_password_success");

    companion object {
        /**
         * @return Dynamic HTML ID for indexing multiple radio choices.
         */
        fun radioChoiceIndex(index: Index) = "radio_choice_${index.value}_index"

        /**
         * @return Dynamic HTML ID for indexing multiple questions.
         */
        fun questionIndex(index: Index) = "question_${index.value}_index"

        /**
         * @return Dynamic HTML ID for indexing multiple questions.
         */
        fun questionId(uuid: Uuid) = "question_id_$uuid"
    }
}
